import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { SheetKitProps } from '@/shared/forms/types';
import { cn } from '@/shared/utils/cn';

import { Button } from './Button';
import { ConfirmDiscard } from './ConfirmDiscard';
import { Icon } from './Icon';

/** UI-3 — the form drawer widths (R-050 Drawer 480; GS-35 520; GS-37 560). */
const FORM_WIDTHS = {
  480: 'w-[min(78vw,var(--layout-drawer-w))]',
  520: 'w-[min(78vw,32.5rem)]',
  560: 'w-[min(78vw,35rem)]',
} as const;

export interface SheetProps extends SheetKitProps {
  open: boolean;
  /** For the control that opens it (`aria-controls`). */
  id?: string;
  /** The sheet's accessible name — and its visible heading unless `hideTitle`. */
  title: string;
  /** R-050 drawer head: a meta line under the title (a record's code, say). */
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * `start` / `end`: a panel from that inline edge (it follows `dir`, so
   * `start` is the right edge in Arabic). `bottom`: a sheet from the bottom
   * edge, the phone's form and filter surface.
   */
  side?: 'start' | 'end' | 'bottom';
  /** Keep the title as the accessible name only — when the content brings its own heading. */
  hideTitle?: boolean;
  /**
   * `shell`: the Deep Plum navigation surface, with its own ink and hairlines
   * (R-050 shell specification) — the phone's navigation drawer.
   */
  tone?: 'default' | 'shell';
  /**
   * One control in the header, before the close control — the filter drawer's
   * "Clear all" (addendum 04 §Filters: "Clear all sits in the drawer header").
   */
  headerAction?: ReactNode;
}

/**
 * DS-02 — the Sheet / Drawer primitive (R-050 addendum reconciliation: "build
 * it first; it is the critical path" — four addendum components compose it).
 *
 * Built on the native `<dialog>` opened with `showModal()`, like `Modal`: the
 * platform makes everything behind it inert, traps focus inside it and turns
 * Escape into a `cancel` event, so none of that is re-implemented here. What is
 * added:
 *
 *   • the scrim is R-050's `--overlay-scrim` (Deep Plum at 44%), and a press on
 *     it closes the sheet;
 *   • focus goes back to whatever opened it, on every way of closing;
 *   • the panel is titled for assistive technology (`aria-labelledby`), with a
 *     44px close control that says what it closes.
 *
 * Stacking follows R-050: a sheet is a drawer (41), below dialogs (61), so a
 * confirmation opened from a sheet stays reachable.
 *
 * UI-3 — a FORM drawer (`width` given) is a panel from the inline-end edge at
 * its R-050 width, and below 640px a bottom sheet capped at 94% (addendum 03
 * §6: "Near-full-height bottom sheet … Not promoted to a full page"). One that
 * holds unsaved input (`dirty`) asks before Escape, the scrim or Close throws
 * it away; one with a save in flight (`busy`) stays until it settles.
 */
export function Sheet({
  open,
  id,
  title,
  subtitle,
  onClose,
  children,
  footer,
  side = 'end',
  hideTitle = false,
  tone = 'default',
  headerAction,
  width,
  dirty = false,
  busy = false,
}: SheetProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const titleId = useId();
  const [confirming, setConfirming] = useState(false);
  const formDrawer = width !== undefined && side !== 'bottom';

  useEffect(() => {
    const dialog = ref.current;

    if (!dialog) return;

    if (open && !dialog.open) {
      opener.current = document.activeElement;
      dialog.showModal();
    }

    if (!open && dialog.open) dialog.close();
  }, [open]);

  const restoreFocus = () => {
    const element = opener.current;
    opener.current = null;

    if (element instanceof HTMLElement && element.isConnected) element.focus();
  };

  /** Escape, the scrim and the head's Close — never the caller's own buttons. */
  const requestClose = () => {
    if (busy) return;
    if (dirty) {
      setConfirming(true);
      return;
    }
    onClose();
  };

  return (
    <>
      <dialog
        ref={ref}
        id={id}
        aria-labelledby={titleId}
        aria-busy={busy || undefined}
        onCancel={(event) => {
          /*
           * React carries a nested dialog's `cancel` and `close` up the component
           * tree although the platform does not bubble them: a dialog opened from
           * inside this sheet must not close it, nor send focus back to its opener.
           */
          if (event.target !== event.currentTarget) return;
          // Escape: close through the owner, so its state stays the truth.
          event.preventDefault();
          requestClose();
        }}
        onClose={(event) => {
          if (event.target === event.currentTarget) restoreFocus();
        }}
        onClick={(event) => {
          // A press on the scrim lands on the dialog element itself; a press on
          // the panel lands on something inside it.
          if (event.target === event.currentTarget) requestClose();
        }}
        className={cn(
          // `open:flex`, never `flex`: a closed <dialog> is hidden only by the
          // browser's own `display: none`, which an author `display` would beat.
          'fixed z-[41] m-0 max-w-none flex-col p-0 shadow-[var(--shadow-lg)] open:flex',
          tone === 'shell' ? 'bg-shell text-ink-on-shell' : 'bg-card text-ink',
          'backdrop:bg-[var(--overlay-scrim)]',
          /*
           * The height rule is chosen per side, never overridden (request
           * w6-to-w1-4): with `max-h-none` in the base, `cn()` kept BOTH it and
           * the bottom sheet's `max-h-[88dvh]` (tailwind-merge does not collapse
           * the two), and `max-h-none` comes later in the stylesheet — so a
           * bottom sheet with long content grew past the screen, its header
           * above the top edge and its body never scrolling. A side sheet takes
           * the full height (`max-h-none` beats the browser's own cap on a modal
           * dialog); a bottom sheet is capped at 88% and scrolls its body.
           */
          side === 'bottom'
            ? // Addendum 04 §Overlays: a bottom sheet with a fixed footer, capped at 88%.
              'inset-x-0 top-auto bottom-0 max-h-[88dvh] w-full rounded-t-[var(--radius-lg)] pb-[env(safe-area-inset-bottom)]'
            : cn('inset-y-0 h-dvh max-h-none', FORM_WIDTHS[width ?? 480]),
          side === 'start' && 'start-0 end-auto',
          side === 'end' && 'end-0 start-auto',
          // A form drawer on a phone: the near-full-height bottom sheet (94%).
          formDrawer &&
            'max-sm:start-0 max-sm:end-0 max-sm:top-auto max-sm:bottom-0 max-sm:h-auto max-sm:max-h-[94dvh] max-sm:w-full max-sm:rounded-t-[var(--radius-lg)] max-sm:pb-[env(safe-area-inset-bottom)]',
        )}
      >
        <div
          className={cn(
            'flex items-center gap-2 px-4 py-3',
            hideTitle ? 'justify-end' : 'justify-between border-b',
            tone === 'shell' ? 'border-line-on-shell' : 'border-line-subtle',
          )}
        >
          <div className={cn('min-w-0', hideTitle && 'sr-only')}>
            <h2 id={titleId} className="text-base font-semibold wrap-break-word">
              {title}
            </h2>
            {subtitle ? (
              <p
                className={cn(
                  'type-meta mt-0.5',
                  tone === 'shell' ? 'text-ink-on-shell' : 'text-ink-muted',
                )}
              >
                {subtitle}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-1">
            {headerAction}
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'min-w-11',
                tone === 'shell' && 'text-ink-on-shell hover:bg-shell-hover active:bg-shell-active',
              )}
              onClick={requestClose}
              aria-label={t('a11y.closeNamed', { name: title })}
              aria-disabled={busy || undefined}
            >
              <Icon name="close" size={16} />
            </Button>
          </div>
        </div>

        {/*
          `flex-auto`, never `flex-1` (R-061 DEF-020 / DEF-025). A bottom sheet
          has no height of its own — it is as tall as its content, up to the cap
          — and with a 0% basis the WebKit of Safari 17.4 to 26.0 (every browser
          on those iPhones) sized it without its body: the sheet was its header
          and Close and nothing else. WebKit 26.5 and Chromium lay out either.
          Starting from the content's own size is the same in every engine;
          `min-h-0` still lets the body shrink and scroll past the cap.
        */}
        {/* The body is a scroller of its own: a table inside it sticks its headings to the
            body's top, not under the page's top bar (DEF-008). */}
        <div className="min-h-0 flex-auto [--sticky-top-offset:0px] overflow-y-auto">
          {children}
        </div>

        {footer ? (
          <div className="flex flex-wrap justify-end gap-2 border-t border-line-subtle px-4 py-3">
            {footer}
          </div>
        ) : null}
      </dialog>

      {dirty ? (
        <ConfirmDiscard
          open={open && confirming}
          onKeep={() => setConfirming(false)}
          onDiscard={() => {
            setConfirming(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}
